import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { QrcodeChannelItem } from '../_components/ChannelConfig/types';
import { getClawChannelConfig, pollClawChannelBindFlow, setClawChannelConfig, startClawChannelBindFlow } from '../apis';

type BindStatus = 'idle' | 'loading' | 'polling' | 'success' | 'error' | 'expired';

interface UseChannelBindOptions {
  clawId: string;
  channelId: string;
  provider: QrcodeChannelItem['provider'];
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const POLL_INTERVAL = 2000;
const QR_EXPIRY_TIME = 5 * 60 * 1000;
type TimerRef = ReturnType<typeof setTimeout>;

export const useChannelBind = ({ clawId, channelId, provider, onSuccess, onError }: UseChannelBindOptions) => {
  const t = useTranslations('claw.channelConfig');
  const [status, setStatus] = useState<BindStatus>('idle');
  const [qrDataUri, setQrDataUri] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [enabled, setEnabled] = useState(true);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isConfigSaving, setIsConfigSaving] = useState(false);
  const pollingTimerRef = useRef<TimerRef | null>(null);
  const expiryTimerRef = useRef<TimerRef | null>(null);
  const isPollingActiveRef = useRef(false);
  const isStartingBindRef = useRef(false);
  const bindRequestIdRef = useRef(0);
  const configRequestIdRef = useRef(0);

  const clearTimers = useCallback(() => {
    isPollingActiveRef.current = false;

    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  const handleError = useCallback(
    (message: string) => {
      setStatus('error');
      setErrorMessage(message);
      clearTimers();
      onError?.(message);
    },
    [clearTimers, onError],
  );

  const startPolling = useCallback(
    (id: string, requestId: number) => {
      clearTimers();
      isPollingActiveRef.current = true;

      const scheduleNextPoll = () => {
        if (!isPollingActiveRef.current) {
          return;
        }

        pollingTimerRef.current = setTimeout(() => {
          poll();
        }, POLL_INTERVAL);
      };

      const poll = async (): Promise<void> => {
        if (!isPollingActiveRef.current || requestId !== bindRequestIdRef.current) {
          return;
        }

        try {
          const { data, error } = await pollClawChannelBindFlow(id);

          if (!isPollingActiveRef.current || requestId !== bindRequestIdRef.current) {
            return;
          }

          if (error) {
            handleError(error.message || t('errors.pollBindStatus'));
            return;
          }

          if (data?.status === 'success') {
            setStatus('success');
            clearTimers();
            onSuccess?.();
          } else if (data?.status === 'error') {
            handleError(data.error_message || t('errors.bindFailed'));
          } else if (!data?.status) {
            handleError(t('errors.invalidResponse'));
          } else {
            scheduleNextPoll();
          }
        } catch (_error) {
          if (!isPollingActiveRef.current || requestId !== bindRequestIdRef.current) {
            return;
          }

          handleError(t('errors.network'));
        }
      };

      poll();

      expiryTimerRef.current = setTimeout(() => {
        if (!isPollingActiveRef.current || requestId !== bindRequestIdRef.current) {
          return;
        }

        setStatus('expired');
        clearTimers();
        onError?.(t('errors.qrExpired'));
      }, QR_EXPIRY_TIME);
    },
    [clearTimers, handleError, onError, onSuccess, t],
  );

  const startBind = useCallback(async () => {
    if (isStartingBindRef.current) {
      return;
    }

    const requestId = bindRequestIdRef.current + 1;
    bindRequestIdRef.current = requestId;
    isStartingBindRef.current = true;
    setStatus('loading');
    setQrDataUri('');
    setErrorMessage('');

    try {
      const { data, error } = await startClawChannelBindFlow({
        claw_id: clawId,
        provider,
      });

      if (requestId !== bindRequestIdRef.current) {
        return;
      }

      if (error) {
        handleError(error.message || t('errors.startBindFlow'));
        return;
      }

      if (data?.flow_id && data?.qr_data_uri) {
        setQrDataUri(data.qr_data_uri);
        setStatus('polling');
        startPolling(data.flow_id, requestId);
      } else {
        handleError(t('errors.invalidResponse'));
      }
    } catch (_error) {
      if (requestId !== bindRequestIdRef.current) {
        return;
      }

      handleError(t('errors.network'));
    } finally {
      if (requestId === bindRequestIdRef.current) {
        isStartingBindRef.current = false;
      }
    }
  }, [clawId, handleError, provider, startPolling]);

  const loadEnabled = useCallback(async (): Promise<{ enabled: boolean }> => {
    const requestId = configRequestIdRef.current + 1;
    configRequestIdRef.current = requestId;
    setIsConfigLoading(true);
    let nextEnabled = true;

    try {
      const { data, error } = await getClawChannelConfig(clawId, channelId);

      if (requestId !== configRequestIdRef.current) {
        return { enabled: nextEnabled };
      }

      if (error) {
        onError?.(error.message || t('errors.loadConfig'));
        return { enabled: nextEnabled };
      }

      const nextConfig = data?.config ?? {};
      setConfig(nextConfig);
      const configEnabled = nextConfig.enabled;
      nextEnabled = typeof configEnabled === 'boolean' ? configEnabled : true;
      setEnabled(nextEnabled);
    } catch (_error) {
      if (requestId !== configRequestIdRef.current) {
        return { enabled: nextEnabled };
      }

      onError?.(t('errors.network'));
    } finally {
      if (requestId === configRequestIdRef.current) {
        setIsConfigLoading(false);
      }
    }

    return { enabled: nextEnabled };
  }, [channelId, clawId, onError, t]);

  const updateEnabled = useCallback(
    async (nextEnabled: boolean): Promise<{ error?: string }> => {
      const requestId = configRequestIdRef.current + 1;
      configRequestIdRef.current = requestId;
      setIsConfigSaving(true);

      try {
        const { error } = await setClawChannelConfig(channelId, {
          claw_id: clawId,
          config: {
            ...config,
            enabled: nextEnabled,
          },
        });

        if (requestId !== configRequestIdRef.current) {
          return {};
        }

        if (error) {
          return { error: error.message || t('errors.saveConfig') };
        }

        setEnabled(nextEnabled);
        setConfig((currentConfig) => ({
          ...currentConfig,
          enabled: nextEnabled,
        }));
        return {};
      } catch (_error) {
        if (requestId !== configRequestIdRef.current) {
          return {};
        }

        return { error: t('errors.network') };
      } finally {
        if (requestId === configRequestIdRef.current) {
          setIsConfigSaving(false);
        }
      }
    },
    [channelId, clawId, config],
  );

  const reset = useCallback(() => {
    bindRequestIdRef.current += 1;
    configRequestIdRef.current += 1;
    clearTimers();
    setStatus('idle');
    setQrDataUri('');
    setErrorMessage('');
    isStartingBindRef.current = false;
  }, [clearTimers]);

  useEffect(() => {
    return () => {
      clearTimers();
      isStartingBindRef.current = false;
    };
  }, [clearTimers]);

  return {
    status,
    qrDataUri,
    errorMessage,
    enabled,
    isConfigLoading,
    isConfigSaving,
    loadEnabled,
    updateEnabled,
    startBind,
    reset,
  };
};
